<template>
  <div class="incident-view">
    <v-card
      class="white elevation-3 ma-4"
      :loading="loading"
    >
      <toolbar
        v-if="entity.incident"
        :show-technical-info="$can('seeTechnicalInfo', 'incident')"
        :show-flag-btn="canAddFlag(entity.incident)"
        :show-edit-btn="$can('edit', 'incident') && !entity.incident.readOnly"
        :incident="entity.incident"
        :saving="saving"
        :validating="validating"
        :validation-supports="validationSupports"
        :validated="validated"
        :downloading="downloading || pdfWaiting"
        :edit-mode.sync="editMode"
        :save-disabled="!incidentIsChanged"
        @clickFlag="addFlag(entity.incident)"
        @download="downloadPdf(entity.incident.id, 'general', null)"
        @clickTechnicalInfo="$refs.technicalInfoPopup.show()"
        @cancelEdit="cancelEdit"
        @save="saveIncidentData()"
        @validate="validateIncidentData()"
        @clickEdit="validated = false"
      />
      <v-divider/>
      <v-row>
        <v-col
          md="3"
          class="pt-0 pb-0"
        >
          <categories
              ref="categories"
              :value.sync="activeTab"
              :height="containersHeight"
              :categories="categories"
          />
        </v-col>

        <v-col
          md="5"
          class="pt-0 pb-0 pr-0"
        >
          <v-container
            v-if="entity.requestCode === 403 || entity.requestCode === 404"
            class="bg fill-height grid-list-md text-xs-center"
          >
            <v-layout class="row wrap align-center">
              <v-flex>
                <h2 class="text-center">The incident does not exist</h2>
                <p class="text-center"><a href="/incidents">Back to incidents</a></p>
              </v-flex>
            </v-layout>
          </v-container>

          <v-tabs-items v-model="activeTab">
            <v-tab-item
                v-for="(item, index) in incidentFields"
                v-bind:key="'tab_' + index"
            >
              <div v-if="activeTab === index">
                <edit-category-fields
                    v-if="editMode"
                    :height="containersHeight"
                    :field-list="item.fields"
                    :incident-data.sync="incidentDataClone"
                    :category-slug="item.categorySlug"
                    :client-id="entity.incident.clientId"
                />
                <view-category-fields
                  v-else
                  :height="containersHeight"
                  :field-list="item.fields"
                  :incident="entity.incident"
                  :category-slug="item.categorySlug"
                />
              </div>
            </v-tab-item>
          </v-tabs-items>
        </v-col>

        <v-col
          md="4"
          class="pt-0 pb-0 pl-0"
        >
          <div
            class="right-container"
            v-bind:style="{ height: containersHeight + 'px' }"
          >
            <flag-info
              v-if="entity.incident && entity.incident.flag"
              ref="flagInfo"
              :flag="entity.incident.flag"
              :incident="entity.incident"
              :can-edit="canAddFlag(entity.incident)"
              :can-see-participants="$can('seeParticipants', 'flag')"
              :loading="flagInfoLoading"
              class="mb-5"
              @deleteAlert="deleteAlert"
              @deleteLastAlert="deleteLastAlert"
              @clickEdit="addFlag(entity.incident)"
              @clickUnderstand="$refs.respondFlagPopup.show(true)"
              @clickDidNotUnderstand="$refs.respondFlagPopup.show(false)"
              @deleteComment="deleteComment"
              @addComment="$refs.addFlagCommentPopup.show()"
              @closePendingFlag="$refs.closePendingFlagPopup.show()"
            />
            <gallery
              v-if="documentItems.length"
              title="Documents"
              icon="mdi-file-document"
              :items="documentItems"
              :check-webp="true"
              class="mb-5"
              @click="showImage"
            />
            <gallery
              v-if="imagesItems.length"
              title="Images"
              icon="mdi-file-image"
              :items="imagesItems"
              :check-webp="false"
              class="mb-5"
              @click="showImage"
            />
            <last-updates-logs
              v-if="entity.incident && entity.incident.incidentAudits.length"
              :items="entity.incident.incidentAudits"
              :loading="incidentReloading"
              class="mb-5"
            />
            <transmission-history
              v-if="entity.incident"
              :items="entity.transmissionHistory || []"
              :loading="loadingTransmissionHistory"
              class="mb-5"
            />
            <files
              v-if="entity.incident"
              :incidentId="entity.incident.id"
              class="mb-5"
            />
            <blood-transfusion-signature
                v-if="entity.incident && entity.incident.hasBloodTransfusion
                  && (
                      $store.state.user.licenseTypeId === lisenceTypes.PHYSICIAN
                      || entity.incident.bloodTransfusionSigned
                  )
                  && !editMode"
                ref="bloodTransfusionSignature"
                :loading="incidentReloading || loading"
                :incident-id="entity.incident.id"
                :incident-data="entity.incident.data"
                :blood-transfusion-signed="bloodTransfusionSigned"
                :blood-transfusion-signature="bloodTransfusionSignature"
                @goToBloodTransfusion="goToBloodTransfusion"
                @documentCreated="setBloodTransfusion"
            />
          </div>
        </v-col>
      </v-row>
    </v-card>
    <add-flag-popup
        ref="addFlagPopup"
        @sent="reloadFlag()"
    />
    <respond-flag-popup
      v-if="entity.incident"
      ref="respondFlagPopup"
      :incident-id="entity.incident.id"
      @responded="reloadFlag()"
    />
    <confirmation ref="confirmationPopup"/>
    <save-reason-popup ref="saveReasonPopup"/>
    <technical-info-popup
      v-if="entity.incident"
      ref="technicalInfoPopup"
      :incident="entity.incident"
      :show-actions="$store.getters.isImpersonated"
    />
    <view-incident-popup
        ref="viewIncidentPopup"
        @add-flag="addFlag"
    />
    <validation-popup
      v-if="entity.incident"
      ref="validationPopup"
      :incidentId="entity.incident.id"
      @validated="validated = true"
    />
    <pre-save-validation-popup
      ref="preSaveValidationPopup"
      @save="saveIncidentData(true)"
      @startValidation="validateIncidentData"
    />
    <add-flag-comment-popup
      v-if="entity.incident"
      ref="addFlagCommentPopup"
      :incidentId="entity.incident.id"
      @sentComment="reloadFlag()"
    />
    <close-pending-flag-popup
      v-if="entity.incident"
      ref="closePendingFlagPopup"
      :incidentId="entity.incident.id"
      @closedPendingFlag="reloadFlag()"
    />
  </div>
</template>

<script>
import _ from 'lodash';
import { createNamespacedHelpers, mapActions } from 'vuex';
import {
  LOAD_INCIDENT_FIELDS, LOAD_INCIDENT, RELOAD_FLAG_INFO,
  LOAD_TRANSMISSION_HISTORY, RELOAD_INCIDENT_AFTER_SAVE,
} from '@/store/IncidentView/actions';
import { CLEAR_STATE, SET_INCIDENT } from '@/store/IncidentView/mutations';
import Categories from '@/components/IncidentView/Categories';
import ViewCategoryFields from '@/components/IncidentView/ViewCategoryFields';
import Gallery from '@/components/IncidentView/Gallery';
import FlagInfo from '@/components/IncidentView/FlagInfo';
import Toolbar from '@/components/IncidentView/Toolbar';
import incidentFlagMixins from '@/mixins/incidentFlagMixins';
import downloadPdf from '@/mixins/downloadPdf';
import AddFlagPopup from '@/components/FlaggedUsers/AddFlagPopup';
import EditCategoryFields from '@/components/IncidentView/EditCategoryFields';
import LastUpdatesLogs from '@/components/IncidentView/LastUpdatesLogs';
import TransmissionHistory from '@/components/IncidentView/TransmissionHistory';
import BloodTransfusionSignature from '@/components/IncidentView/BloodTransfusionSignature';
import Confirmation from '@/components/Confirmation';
import incidents from '@/api/incidents';
import SaveReasonPopup from '@/components/IncidentView/SaveReasonPopup';
import RespondFlagPopup from '@/components/IncidentView/RespondFlagPopup';
import AddFlagCommentPopup from '@/components/IncidentView/AddFlagCommentPopup';
import ClosePendingFlagPopup from '@/components/IncidentView/ClosePendingFlagPopup';
import TechnicalInfoPopup from '@/components/IncidentView/TechnicalInfoPopup';
import ViewIncidentPopup from '@/components/Incidents/ViewIncidentPopup';
import Files from '@/components/IncidentView/Files';
import flaggedUsers from '@/api/flaggedUsers';
import ValidationPopup from '@/components/IncidentView/ValidationPopup';
import PreSaveValidationPopup from '@/components/IncidentView/PreSaveValidationPopup';
import LICENSE_TYPES from '@/enums/licenseTypes';

const { mapMutations } = createNamespacedHelpers('incidentView');

export default {
  name: 'IncidentViewClient',
  mixins: [incidentFlagMixins, downloadPdf],
  components: {
    PreSaveValidationPopup,
    ValidationPopup,
    ViewIncidentPopup,
    TechnicalInfoPopup,
    AddFlagCommentPopup,
    ClosePendingFlagPopup,
    RespondFlagPopup,
    SaveReasonPopup,
    Confirmation,
    TransmissionHistory,
    BloodTransfusionSignature,
    LastUpdatesLogs,
    EditCategoryFields,
    AddFlagPopup,
    Toolbar,
    FlagInfo,
    Gallery,
    ViewCategoryFields,
    Categories,
    Files,
  },
  data() {
    return {
      activeTab: 0,
      windowHeight: window.innerHeight,
      indentHeight: 200,
      loading: false,
      loadingTransmissionHistory: false,
      flagInfoLoading: false,
      incidentReloading: false,
      editMode: false,
      incidentDataClone: null,
      saving: false,
      validating: false,
      validated: false,
      lisenceTypes: LICENSE_TYPES,
      bloodTransfusionSigned: false,
      bloodTransfusionSignature: null,
    };
  },
  computed: {
    echoChannel() {
      return `incident.${this.entity.incident.id}`;
    },

    /**
     * Get incident view full state.
     *
     * @return {Object}
     */
    entity() {
      return this.$store.state.incidentView;
    },

    /**
     * Is incident changed.
     *
     * @return {Boolean}
     */
    incidentIsChanged() {
      const origData = _.cloneDeep(this.entity.incident.data);
      _.forEach(this.incidentDataClone, (value, field) => {
        if (!Object.prototype.hasOwnProperty.call(origData, field)) {
          origData[field] = null;
        }
      });
      return !_.isEqualWith(this.incidentDataClone, origData, (value1, value2) => {
        if (this.bothIsEmpty(value1, value2)) {
          return true;
        }
        if (this.equalNumeric(value1, value2)) {
          return true;
        }
        return undefined;
      });
    },

    /**
     * Calculates height of containers.
     *
     * @return {Number}
     */
    containersHeight() {
      return this.windowHeight - this.indentHeight;
    },

    /**
     * Check if the data contains Blood Product medication.
     *
     * @return {Boolean}
     */
    hasBloodProduct() {
      let hasBloodProduct = false;
      for (let i = 0; i < 9; i++) {
        const fieldName = `MD_Meds${i}`;
        const pnName = `MD_PertinentNegatives${i}`;
        const pn = Object.prototype.hasOwnProperty.call(this.entity.incident.data, pnName)
          ? this.entity.incident.data[pnName]
          : null;
        if (Object.prototype.hasOwnProperty.call(this.entity.incident.data, fieldName)
            && this.entity.incident.data[fieldName] === 'BP'
            && !pn
        ) {
          hasBloodProduct = true;
        }
      }
      return hasBloodProduct;
    },

    /**
     * Get list of category names.
     *
     * @return {Array}
     */
    categories() {
      return this.incidentFields.map(item => item.categoryName);
    },

    /**
     * Get list of filtered incident fields.
     *
     * @return {Array}
     */
    incidentFields() {
      return this.entity.incidentFields.filter(item => {
        if (item.categoryName === 'Blood Transfusion') {
          return this.hasBloodProduct && this.entity.incident.bloodTransfusionSupports;
        }
        return true;
      });
    },

    /**
     * Get list of page images
     *
     * @return {Array}
     */
    documentItems() {
      const items = [];
      if (this.entity.incident && this.entity.incident.images) {
        this.entity.incident.images.forEach((item, index) => {
          if (item.type === 'page') {
            items.push({
              id: index,
              src: item.thumbnail_url,
            });
          }
        });
      }
      return items;
    },

    /**
     * Get list of images
     *
     * @return {Array}
     */
    imagesItems() {
      const items = [];
      if (this.entity.incident && this.entity.incident.images) {
        this.entity.incident.images.forEach((item, index) => {
          if (item.type !== 'page') {
            items.push({
              id: index,
              src: item.url,
            });
          }
        });
      }
      return items;
    },

    /**
     * Is validation supports.
     *
     * @return {Boolean}
     */
    validationSupports() {
      if (this.entity.incident && this.entity.incident.validationSupports) {
        return true;
      }
      return false;
    },
  },
  watch: {
    incidentDataClone: {
      handler() {
        this.validated = false;
      },
      deep: true,
    },
  },
  async mounted() {
    this.clearState();
    await this.loadData();
    this.activeTab = 0;
    if (window.location.hash === '#edit') {
      this.editMode = true;
    }
    this.loadTransmissionHistoryData();
    this.listenEvents();
  },
  unmounted() {
    this.$echo.leave(this.echoChannel);
  },
  created() {
    window.addEventListener('resize', this.recalculateHeights);
  },
  destroyed() {
    window.removeEventListener('resize', this.recalculateHeights);
  },
  methods: {
    ...mapMutations({
      clearState: CLEAR_STATE,
      setIncident: SET_INCIDENT,
    }),
    ...mapActions({
      loadFields: `incidentView/${LOAD_INCIDENT_FIELDS}`,
      loadIncident: `incidentView/${LOAD_INCIDENT}`,
      loadTransmissionHistory: `incidentView/${LOAD_TRANSMISSION_HISTORY}`,
      reloadFlagInfo: `incidentView/${RELOAD_FLAG_INFO}`,
      reloadAfterSave: `incidentView/${RELOAD_INCIDENT_AFTER_SAVE}`,
    }),

    /**
     * Listen incident events.
     */
    listenEvents() {
      this.$echo.private(this.echoChannel).listen('.BloodTransfusionDocumentSigned', (data) => {
        this.bloodTransfusionSigned = true;
        this.setBloodTransfusion({
          id: data.id,
          documentId: data.documentId,
          image: data.image,
          thumbnail: data.thumbnail,
          status: 'completed',
        });
        this.$refs.bloodTransfusionSignature.setConfirmed();
      });
    },

    setBloodTransfusion(data) {
      this.bloodTransfusionSignature = data;
    },

    /**
     * Go To blood transfusion.
     */
    goToBloodTransfusion() {
      this.$refs.categories.goToCategory('Blood Transfusion');
    },

    /**
     * Delete flagged user.
     *
     * @param alert - Flag alert object
     */
    deleteAlert(alert) {
      this.$refs.confirmationPopup.showConfirm(
        'Confirmation',
        `Are you sure you want to delete flagged user "${alert.user.firstName} ${alert.user.lastname}"?`,
        async () => {
          await flaggedUsers.deleteAlert(alert.incidentId, alert.alertId);
          this.$refs.flagInfo.alertDeleted(alert.alertId);
          this.reloadFlag();
        },
      );
    },

    /**
     * Delete last flagged user (delete whole flag).
     *
     * @param alert - Flag alert object
     */
    deleteLastAlert(alert) {
      this.$refs.confirmationPopup.showConfirm(
        'Confirmation',
        'It is the last flagged user, the flag cannot exist without users.'
          + ' Do you confirm deleting the entire flag?',
        async () => {
          await flaggedUsers.deleteFlag(alert.incidentId);
          this.$refs.flagInfo.alertDeleted(alert.alertId);
          this.reloadFlag();
        },
      );
    },

    /**
     * Check two values, and returns true if both is empty.
     *
     * @param value1 - First value
     * @param value2 - Second value
     */
    bothIsEmpty(value1, value2) {
      const firstIsEmpty = value1 === null || value1 === undefined || value1 === '';
      const secondIsEmpty = value2 === null || value2 === undefined || value2 === '';
      return firstIsEmpty && secondIsEmpty;
    },

    /**
     * Check two numeric values with comparing like string.
     *
     * @param value1 - First value
     * @param value2 - Second value
     */
    equalNumeric(value1, value2) {
      const first = typeof value1 === 'number' ? value1.toString() : value1;
      const second = typeof value2 === 'number' ? value2.toString() : value2;
      return first === second;
    },

    /**
     * Reload flag information
     */
    async reloadFlag() {
      try {
        this.flagInfoLoading = true;
        await this.reloadFlagInfo(this.$route.params.id);
      } finally {
        this.flagInfoLoading = false;
      }
    },

    /**
     * Reload incident information
     */
    async reloadIncidentAfterSave() {
      try {
        this.incidentReloading = true;
        await this.reloadAfterSave(this.$route.params.id);
        this.bloodTransfusionSigned = !!this.entity.incident.bloodTransfusionSigned;
        this.bloodTransfusionSignature = this.entity.incident.bloodTransfusionSignature;
      } finally {
        this.incidentReloading = false;
      }
    },

    /**
     * Load all data of incident.
     */
    async loadData() {
      try {
        this.loading = true;
        const promises = [
          this.loadFields(this.$route.params.id),
          this.loadIncident(this.$route.params.id),
        ];
        await Promise.all(promises);
        this.freshClone();
        this.bloodTransfusionSigned = !!this.entity.incident.bloodTransfusionSigned;
        this.bloodTransfusionSignature = this.entity.incident.bloodTransfusionSignature;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Load transmission history data.
     */
    async loadTransmissionHistoryData() {
      try {
        this.loadingTransmissionHistory = true;
        await this.loadTransmissionHistory(this.$route.params.id);
      } catch (e) {
        console.log(e, 'load transmission history error.');
      } finally {
        this.loadingTransmissionHistory = false;
      }
    },

    /**
     * Refresh cloned data.
     */
    freshClone() {
      this.incidentDataClone = _.cloneDeep(this.entity.incident.data);
    },

    /**
     * Recalculate height of window, handled by change page size.
     */
    recalculateHeights() {
      this.windowHeight = window.innerHeight;
    },

    /**
     * Cancel edit handler.
     */
    cancelEdit() {
      if (!this.incidentIsChanged) {
        this.editMode = false;
        return;
      }
      this.$refs.confirmationPopup.showConfirm(
        'Confirmation',
        'Your changes can be lost',
        () => {
          this.editMode = false;
          this.freshClone();
        },
      );
    },

    /**
     * Get changed data to save.
     */
    getDataToSave() {
      const data = {};
      _.forEach(this.incidentDataClone, (value, field) => {
        if (typeof value === 'object' && value !== null) {
          if (!_.isEqual(value, this.entity.incident.data[field])) {
            data[field] = value;
          }
          return;
        }
        if (this.bothIsEmpty(value, this.entity.incident.data[field])) {
          return;
        }
        if (value !== this.entity.incident.data[field]) {
          data[field] = value === '' ? null : value;
        }
      });
      return data;
    },

    /**
     * Save incident data.
     */
    async saveIncidentData(skip = false) {
      if (this.validationSupports && !this.validated && !skip) {
        this.$refs.preSaveValidationPopup.show();
        return;
      }
      this.$refs.saveReasonPopup.show(async (reason) => {
        try {
          this.saving = true;
          await incidents.saveIncidentData(this.$route.params.id, {
            reason,
            data: this.getDataToSave(),
          });
          this.entity.incident.data = _.cloneDeep(this.incidentDataClone);
          this.setIncident(this.entity.incident);
          this.freshClone();
          this.editMode = false;
          this.reloadIncidentAfterSave();
        } finally {
          this.saving = false;
        }
      });
    },

    /**
     * Validate incident data.
     */
    async validateIncidentData() {
      try {
        this.validating = true;
        await incidents.validateIncidentData(this.$route.params.id, {
          data: this.getDataToSave(),
        });
        this.$refs.validationPopup.waitValidationResult();
      } finally {
        this.validating = false;
      }
    },

    /**
     * Show page or image, opens view incident popup.
     */
    showImage(id) {
      const options = {
        showViewIncidentLink: false,
        showActions: true,
        showNonRegularActions: false,
      };
      this.$refs.viewIncidentPopup.show(
        this.entity.incident,
        options,
        id,
      );
    },

    /**
     * Delete flag comment.
     *
     * @param comment - Flag comment object
     */
    deleteComment(comment) {
      this.$refs.confirmationPopup.showConfirm(
        'Confirmation',
        'Are you sure you want to delete comment?',
        async () => {
          await flaggedUsers.deleteFlagComment(this.entity.incident.id, comment.id);
          this.reloadFlag();
        },
      );
    },
  },
};
</script>

<style lang="scss">
.right-container {
  background-color: #f5f5f6;
  border-left: 1px solid #e1e1e3;
  padding: 20px;
  overflow-y: scroll;
}
</style>
